/**
 * PojisteniModal — the insurance blocks of one fleet car (per-section modal of the fleet row). A member
 * with `pujcovna:write` edits and saves; `read` sees the same blocks without a save button.
 */

import { useMemo } from 'react';
import { Button } from '@/components/Button';
import { Modal, DialogCrown } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { usePojisteniQuery, useSavePojisteniMutation } from './api';
import {
  POJISTENI_FORM_ID,
  PojisteniFormBody,
  pojisteniChanges,
  pojisteniProblem,
  pojisteniSeed,
} from './pojisteni-section-forms';

interface PojisteniModalProps {
  /** The fleet entry id. */
  id: number;
  /** What the dialog titles the car with (plate or VIN). */
  title: string;
  canWrite: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PojisteniModal({ id, title, canWrite, open, onOpenChange }: PojisteniModalProps) {
  const query = usePojisteniQuery(id);
  const save = useSavePojisteniMutation(id);
  // Every way out (Zrušit, ×, Esc, a click outside, the sheet's drag) ends in `onOpenChange(false)`; while the
  // save is pending it would unmount the observer the success / error toasts hang on, so it waits.
  const guarded = (next: boolean) => {
    if (!next && save.isPending) return;
    onOpenChange(next);
  };
  // A form seeded from "no data" would offer five empty blocks and a save would overwrite the real ones
  // unseen, and so would one seeded from a cached snapshot that another member has since changed: the modal
  // mounts per open, so the body waits for a read that finished AFTER this mount (`isFetchedAfterMount`),
  // not just for cached data. A failed read blocks everything, a cached snapshot included.
  const fresh = query.isSuccess && query.isFetchedAfterMount;
  const seed = useMemo(() => (fresh && query.data ? pojisteniSeed(query.data) : null), [fresh, query.data]);

  return (
    <Modal open={open} onOpenChange={guarded} maxWidth={720}>
      <DialogCrown
        eyebrow="Vozidlo půjčovny"
        title={
          <>
            Pojištění <em>{title}</em>
          </>
        }
        sub="Pojištění, která jsou součástí nájmu. Limit a spoluúčast se tisknou do nájemní smlouvy."
      />
      <Modal.Body className="px-8 py-5">
        {query.isError ? (
          <p data-testid="pojisteni-error" className="text-[13px] text-ink-2">
            Pojištění se nepodařilo načíst. Zkuste to prosím znovu za chvíli.
          </p>
        ) : !seed ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <PojisteniFormBody
            initial={seed}
            busy={save.isPending}
            disabled={!canWrite}
            onSubmit={(form) => {
              const problem = pojisteniProblem(form);
              if (problem) {
                toast.error(problem);
                return;
              }
              // Only the blocks this user changed are sent (the API upserts just the types it is given).
              const changes = pojisteniChanges(seed, form);
              if (changes.length === 0) {
                onOpenChange(false);
                return;
              }
              save.mutate(changes, {
                onSuccess: () => {
                  toast.ok('Pojištění uloženo.');
                  onOpenChange(false);
                },
                onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se uložit pojištění.'}`),
              });
            }}
          />
        )}
      </Modal.Body>
      <Modal.Foot
        actions={
          canWrite ? (
            <>
              <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={save.isPending}>
                Zrušit
              </Button>
              <Button variant="ink" type="submit" form={POJISTENI_FORM_ID} disabled={save.isPending || !seed}>
                Uložit
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => guarded(false)}>
              Zavřít
            </Button>
          )
        }
      />
    </Modal>
  );
}
